// useStatementActions.js — single source of truth for statement row/panel actions
// (jump to reconcile, download, delete-with-confirm). Used by InvoiceStatementModal's
// toolbar buttons AND the table row menu so behaviour stays identical.
//
// params: { invStatId, dpaId, docId, supplierName, statementDate, grade, activeSection, displayName }
// opts:   { onDeleted }
import { useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import useFetchData from "../components/useFetchDatav2";
import { useToast } from "../Context/useToast";

const initialDialog = { isModalDialogueOpen: false, question: "" };

// Statement PDFs come back from /getfile.sz as a blob; PdfModal wants base64.
function convertBlobToBase64(blob) {
   return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
   });
}

function dialogReducer(state, action) {
   switch (action.type) {
      case "OPEN_MODAL":
         return { ...state, isModalDialogueOpen: true, question: action.payload.question };
      case "CLOSE_MODAL":
         return { ...state, isModalDialogueOpen: false };
      default:
         return state;
   }
}

export default function useStatementActions(params, opts = {}) {
   const { invStatId, dpaId, docId, supplierName, statementDate, grade, activeSection, displayName } = params || {};
   const { onDeleted } = opts;
   const navigate = useNavigate();
   const { fetchData } = useFetchData();
   const { triggerToast } = useToast();

   const [dialogState, dispatchDialog] = useReducer(dialogReducer, initialDialog);
   const [isDeleting, setIsDeleting] = useState(false);
   // Statement-document viewer state (table row menu's "View statement" opens it directly,
   // skipping the summary — same fetch the tile summary's PNG "View" uses).
   const [statementView, setStatementView] = useState({ open: false, imageData: null });

   const viewStatement = async () => {
      if (!invStatId) {
         triggerToast("warning", "No statement document available to view.", false, "Warning", false);
         return;
      }
      try {
         const formData = new FormData();
         formData.append("_inv_stat_id", invStatId);
         const { responseData, fetchError } = await fetchData("/getfile.sz", "POST", formData, "form-data", "blob");
         if (fetchError || !responseData) throw new Error("Failed to retrieve statement document.");
         const imageData = await convertBlobToBase64(responseData);
         setStatementView({ open: true, imageData });
      } catch (error) {
         triggerToast("error", error.message || "Unable to open the statement right now. Please try again.", true, "Issue", false, 0);
      }
   };

   const closeStatementView = () => setStatementView({ open: false, imageData: null });

   const goReconcile = () => {
      if (!invStatId) return;
      navigate("/apps/reconcile", {
         state: {
            selectedInvoiceStatement: invStatId,
            selectedInvoiceStatementSupplierName: supplierName,
            selectedInvoiceStatementDate: statementDate,
            selectedInvoiceStatementGrade: grade ?? "-",
            selectedInvoiceStatementDpaId: dpaId,
            activeSection,
            selectedInvoiceStatementNav: invStatId,
            scrollY: window.scrollY,
         },
      });
   };

   const goDownload = async () => {
      if (!docId) {
         triggerToast("warning", "Unable to download: missing document ID.", false, "Warning", false);
         return;
      }
      try {
         const formData = new FormData();
         formData.append("doc_id", docId);
         const { responseData, fetchError } = await fetchData("/getdownloadurl.sz", "POST", formData, "form-data");
         if (fetchError || !responseData) throw new Error("Network error retrieving download URL.");
         if (responseData.st !== 1) throw new Error(responseData.msg || "Failed to retrieve download URL.");
         if (!responseData.signurl) throw new Error("No download URL provided in response.");
         window.open(responseData.signurl, "_blank");
      } catch (error) {
         triggerToast("error", error.message || "Unable to download document", true, "Issue", false, 0);
      }
   };

   // Opens the confirm dialog. The actual delete fires from onDialogResponse("go").
   const requestDelete = () => {
      const label = displayName || supplierName || "this statement";
      dispatchDialog({ type: "OPEN_MODAL", payload: { question: `Are you sure you want to delete statement ${label}` } });
   };

   const onDialogResponse = async (response) => {
      dispatchDialog({ type: "CLOSE_MODAL" });
      if (response !== "go") return;
      if (!dpaId) {
         triggerToast("error", "Cannot delete: missing document part id.", true, "Error", false, 0);
         return;
      }
      setIsDeleting(true);
      try {
         const formData = new FormData();
         formData.append("_dpa_id", dpaId);
         const { responseData } = await fetchData("/deleteDoc.sz", "POST", formData, "form-data");
         if (responseData?.st === 1) {
            triggerToast("success", "Statement deleted.", false, "", false);
            onDeleted?.(invStatId);
         } else {
            setIsDeleting(false);
            triggerToast("error", responseData?.m_en || "Failed to delete statement.", true, "Error", false, 0);
         }
      } catch {
         setIsDeleting(false);
         triggerToast("error", "Network issue while deleting. Please try again.", true, "Network Issue", false, 0);
      }
   };

   return { goReconcile, goDownload, viewStatement, statementView, closeStatementView, requestDelete, isDeleting, setIsDeleting, dialogState, onDialogResponse };
}
